import { arrayValue } from '../packages/array.js';
import type { HgsConfig, HgsCrossing, HgsVehicleClass, InputError } from '../types/calculation-core.js';
import { DEFAULT_HGS_TOLL_CONFIG, failure, success, resolveMetadata, validateMetadata, isFiniteNumber } from '@hephesapla/core-validation';

type SixEntries<T> = readonly [T, T, T, T, T, T];
type HgsInput = Parameters<typeof import('../types/calculation-core.js').calculateHgsToll>[0];
type HgsResult = ReturnType<typeof import('../types/calculation-core.js').calculateHgsToll>;
interface CrossingTariff { readonly name: string; readonly standardFees: SixEntries<number | null>; readonly nightFees?: SixEntries<number | null>; }
const crossings: readonly HgsCrossing[] = ['15-temmuz', 'fsm', 'yss', 'osmangazi', '1915-canakkale', 'avrasya'];
const vehicleClassIndices: Readonly<Record<HgsVehicleClass, 0 | 1 | 2 | 3 | 4 | 5>> = { 'class-1': 0, 'class-2': 1, 'class-3': 2, 'class-4': 3, 'class-5': 4, 'class-6': 5 };

function sixEntries<T>(value: unknown, valid: (entry: unknown) => entry is T): SixEntries<T> | null {
    const entries = arrayValue(value);
    if (entries === null || entries.length !== 6) return null;
    const cell = (index: number): unknown => Object.hasOwn(entries, index) ? entries[index] : undefined;
    const first = cell(0); if (!valid(first)) return null;
    const second = cell(1); if (!valid(second)) return null;
    const third = cell(2); if (!valid(third)) return null;
    const fourth = cell(3); if (!valid(fourth)) return null;
    const fifth = cell(4); if (!valid(fifth)) return null;
    const sixth = cell(5); if (!valid(sixth)) return null;
    return [first, second, third, fourth, fifth, sixth];
}
function vehicleClassName(value: unknown): value is string { return typeof value === 'string' && value.trim() !== ''; }
function fee(value: unknown): value is number | null { return value === null || (typeof value === 'number' && isFiniteNumber(value)) && value >= 0; }
function clock(value: unknown): boolean { return typeof value === 'string' && /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value); }
function crossingValue(value: unknown): value is HgsCrossing { return crossings.some(crossing => crossing === value); }
function vehicleClassValue(value: unknown): value is HgsVehicleClass { return typeof value === 'string' && Object.hasOwn(vehicleClassIndices, value); }

export function calculateHgsToll(input: HgsInput, config: HgsConfig = DEFAULT_HGS_TOLL_CONFIG): HgsResult {
    const candidateMetadata = config?.metadata;
    const metadata = resolveMetadata(candidateMetadata, DEFAULT_HGS_TOLL_CONFIG.metadata);
    if (!config || typeof config !== 'object') return failure([{
        field: 'config', code: 'INVALID_CONFIG', message: 'HGS konfigürasyonu geçersiz.'
    }], metadata);
    const errors: InputError[] = [...validateMetadata(candidateMetadata)];
    const vehicleClassNames = sixEntries(config.vehicleClassNames, vehicleClassName);
    if (!vehicleClassNames) errors.push({
        field: 'config.vehicleClassNames', code: 'INVALID_CONFIG', message: 'Altı araç sınıfı adı eksiksiz tanımlanmalıdır.'
    });
    const roundTripMultiplier = config.roundTripMultiplier;
    if (!isFiniteNumber(roundTripMultiplier) || roundTripMultiplier <= 0) errors.push({
        field: 'config.roundTripMultiplier', code: 'INVALID_CONFIG', message: 'Gidiş-dönüş çarpanı sıfırdan büyük olmalıdır.'
    });
    const configuredCrossings = config.crossings;
    const tariffs = new Map<HgsCrossing, CrossingTariff>();
    for (const crossing of crossings) {
        const entry = configuredCrossings?.[crossing];
        const field = `config.crossings.${crossing}`;
        if (!entry || typeof entry !== 'object') {
            errors.push({ field, code: 'INVALID_CONFIG', message: 'Geçiş tarife kaydı eksik.' });
            continue;
        }
        const name = entry.name;
        const standardFees = sixEntries(entry.standardFees, fee);
        const configuredNightFees = entry.nightFees;
        const nightFees = configuredNightFees === undefined ? undefined : sixEntries(configuredNightFees, fee);
        const nightWindow = entry.nightWindow;
        if (typeof name !== 'string' || name.trim() === '') errors.push({
            field: `${field}.name`, code: 'INVALID_CONFIG', message: 'Geçiş adı boş olamaz.'
        });
        if (!standardFees) errors.push({
            field: `${field}.standardFees`, code: 'INVALID_CONFIG', message: 'Standart tarife altı sınıf için sayı veya null içermelidir.'
        });
        if (nightFees === null) errors.push({
            field: `${field}.nightFees`, code: 'INVALID_CONFIG', message: 'Gece tarifesi altı sınıf için sayı veya null içermelidir.'
        });
        if (nightWindow !== undefined) {
            let validWindow = clock(nightWindow.startInclusive);
            if (validWindow) validWindow = clock(nightWindow.endExclusive);
            if (validWindow) {
                const timeZone = nightWindow.timeZone;
                validWindow = typeof timeZone === 'string' && timeZone.trim() !== '';
            }
            if (!validWindow) errors.push({
                field: `${field}.nightWindow`, code: 'INVALID_CONFIG', message: 'Gece zaman aralığı ve saat dilimi geçersiz.'
            });
        }
        if (typeof name === 'string' && name.trim() !== '' && standardFees && nightFees !== null) tariffs.set(crossing, { name, standardFees, nightFees });
    }
    if (errors.length > 0 || !vehicleClassNames) return failure(errors, metadata);
    if (!input || typeof input !== 'object') return failure([{
        field: 'input', code: 'REQUIRED', message: 'Hesaplama girdisi gereklidir.'
    }], metadata);
    const crossing = input.crossing, vehicleClass = input.vehicleClass, time = input.time, direction = input.direction;
    if (!crossingValue(crossing)) errors.push({
        field: 'crossing', code: 'UNSUPPORTED_VALUE', message: 'Geçiş noktası desteklenmiyor.'
    });
    if (!vehicleClassValue(vehicleClass)) errors.push({
        field: 'vehicleClass', code: 'UNSUPPORTED_VALUE', message: 'Araç sınıfı class-1 ile class-6 arasında olmalıdır.'
    });
    if (time !== 'gunduz' && time !== 'gece') errors.push({
        field: 'time', code: 'UNSUPPORTED_VALUE', message: 'Geçiş zamanı gündüz veya gece olmalıdır.'
    });
    if (direction !== 'tek' && direction !== 'cift') errors.push({
        field: 'direction', code: 'UNSUPPORTED_VALUE', message: 'Yön tek yön veya gidiş-dönüş olmalıdır.'
    });
    if (errors.length > 0) return failure(errors, metadata);
    const tariff = tariffs.get(crossing);
    if (!tariff) return failure([{
        field: `config.crossings.${crossing}`, code: 'INVALID_CONFIG', message: 'Geçiş tarife kaydı eksik.'
    }], metadata);
    const index = vehicleClassIndices[vehicleClass];
    const name = vehicleClassNames[index];
    const fees = time === 'gece' && tariff.nightFees ? tariff.nightFees : tariff.standardFees;
    const oneWayFee = fees[index];
    if (oneWayFee === null) return success({
        allowed: false, crossingName: tariff.name, vehicleClassName: name, time, direction,
        reason: `${tariff.name}, ${name} araçların geçişine kapalıdır.`
    }, metadata);
    const totalFee = oneWayFee * (direction === 'cift' ? roundTripMultiplier : 1);
    if (!Number.isFinite(totalFee)) return failure([{
        field: 'result', code: 'CALCULATION_OVERFLOW', message: 'Geçiş ücreti sonlu sayı aralığını aştı.'
    }], metadata);
    return success({ allowed: true, crossingName: tariff.name, vehicleClassName: name, oneWayFee, totalFee, time, direction }, metadata);
}
